// Copyright (c) 2025 WiseTime. All rights reserved.

import * as TimelineActions from "../../actions"
import type { RootState } from "../../../../app/store"
import {
  groupPostedTimesheetFieldOptionsBySiteId,
  groupTimesheetFieldOptionsBySiteId,
  splitTimeGroupsByPostStatus,
} from "../../timeGroups"
import { generateTimesheetFieldCachesRefreshActions } from "./generateTimesheetFieldCachesRefreshActions"
import { TimeGroup, TimeRow } from "../../../../generated/wisetime/user/timeline/v4/timeline_pb"

type ProcessTimesheetFieldDataAction = 
  | ReturnType<typeof TimelineActions.generateTimesheetFieldConfigs>
  | ReturnType<typeof TimelineActions.listTimesheetFields>
  | ReturnType<typeof TimelineActions.batchGetTimesheetFieldOptions>

export const processTimesheetFieldData = (
  timeGroups: TimeGroup[], 
  timeRows: TimeRow[],
  state: RootState
): ProcessTimesheetFieldDataAction[] => {
  if (!timeGroups.length && !timeRows.length) {
    return []
  }
  
  const cachedTimesheetFields = Object.values(state.timeline.timesheetFieldsCache)
  const cachedTimesheetFieldOptions = state.timeline.timesheetFieldOptionsCache
  const cachedTimeGroupFieldConfigs = state.timeline.timeGroupFieldConfigsCache
  const actions: ProcessTimesheetFieldDataAction[] = []
  const { posted, unposted } = splitTimeGroupsByPostStatus(timeGroups)
  const unpostedRequiringConfigs = unposted.filter(timeGroup => (
    !cachedTimeGroupFieldConfigs[timeGroup.id] || cachedTimeGroupFieldConfigs[timeGroup.id]?.length === 0
  ))
  if (unpostedRequiringConfigs.length) {
    actions.push(
      TimelineActions.generateTimesheetFieldConfigs(unpostedRequiringConfigs),
    )
  }
  
  const postedOptionsBySiteId = groupPostedTimesheetFieldOptionsBySiteId(posted)
  const timeRowTimesheetOptionsBySiteId = groupTimesheetFieldOptionsBySiteId(timeRows)
  
  // Merge timesheet field options from both time groups and time rows by site ID
  const mergedOptionsBySiteId = { ...postedOptionsBySiteId }
  Object.entries(timeRowTimesheetOptionsBySiteId).forEach(([siteId, options]) => {
    if (mergedOptionsBySiteId[siteId]) {
      mergedOptionsBySiteId[siteId] = [...(mergedOptionsBySiteId[siteId] || []), ...options]
    } else {
      mergedOptionsBySiteId[siteId] = options
    }
  })
  
  const requiredTimesheetFieldActions = generateTimesheetFieldCachesRefreshActions(
    mergedOptionsBySiteId,
    cachedTimesheetFields,
    cachedTimesheetFieldOptions,
  )
  actions.push(...requiredTimesheetFieldActions)
  
  return actions
}
